import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Popupchat from "../components/Popupchat";
import Appfooter from "../components/Appfooter";

import {
  getClubProfileBySlug,
} from "../redux/slices/clubSlice";

import {
  clearEventError,
  clearEventPayment,
  clearEventState,
  createEventPaymentOrder,
  createEventPaymentSession,
  fetchEventDetails,
  fetchEventPaymentStatus,
  registerForEvent,
} from "../redux/slices/eventSlice";

import {
  RootState,
} from "../redux/store";

// ============================================================
// HELPERS
// ============================================================

const formatDateTime = (
  value?: string | null,
) => {
  if (!value) return "-";

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return String(value);
  }

  return date.toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
};

const formatMoney = (
  value: any,
) => {
  const amount =
    Number(value || 0);

  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    },
  ).format(
    Number.isFinite(amount)
      ? amount
      : 0,
  );
};

const getErrorMessage = (
  error: any,
  fallback =
    "Something went wrong",
) => {
  if (!error) {
    return fallback;
  }

  if (
    typeof error ===
    "string"
  ) {
    return error;
  }

  return (
    error?.message ||
    error?.data?.message ||
    fallback
  );
};

const getUserId = (
  user: any,
) =>
  Number(
    user?.user_id ||
      user?.id ||
      0,
  ) || undefined;

const getContactId = (
  user: any,
) =>
  Number(
    user?.contact_id ||
      user?.linked_contact_id ||
      0,
  ) || undefined;

const getUserName = (
  user: any,
) =>
  String(
    user?.full_name ||
      user?.name ||
      user?.user_name ||
      "",
  ).trim();

const getUserMobile = (
  user: any,
) =>
  String(
    user?.mobile_number ||
      user?.mobile ||
      user?.user_mobile ||
      user?.phone_number ||
      "",
  ).trim();

const getUserEmail = (
  user: any,
) =>
  String(
    user?.email ||
      user?.user_email ||
      "",
  ).trim();

const getRegistrationType = (
  user: any,
  event: any,
) => {
  if (!user) {
    return "non_member" as const;
  }

  if (
    Number(
      event?.is_member ||
        0,
    ) === 1
  ) {
    return "member" as const;
  }

  if (getContactId(user)) {
    return "contact" as const;
  }

  return "non_member" as const;
};

// ============================================================
// PAGE
// ============================================================

const EventDetailsPage: React.FC =
  () => {
    const dispatch =
      useDispatch<any>();

    const navigate =
      useNavigate();

    const {
      slug,
      eventId,
    } = useParams();

    const [
      searchParams,
      setSearchParams,
    ] = useSearchParams();

    const {
      clubProfile,
      profileLoading,
    } = useSelector(
      (
        state: RootState,
      ) => state.club,
    );

    const {
      event,
      registration,
      paymentOrder,
      paymentSession,
      paymentStatus,

      eventLoading,
      registrationLoading,
      paymentOrderLoading,
      paymentSessionLoading,
      paymentStatusLoading,

      error,
    } = useSelector(
      (
        state: RootState,
      ) => state.event,
    );

    const loggedUser =
      useMemo(() => {
        try {
          return JSON.parse(
            localStorage.getItem(
              "user",
            ) || "null",
          );
        } catch {
          return null;
        }
      }, []);

    const userId =
      getUserId(loggedUser);

    const contactId =
      getContactId(loggedUser);

    const clientId =
      Number(
        clubProfile?.client_id ||
          0,
      );

    const programId =
      Number(eventId || 0);

    const [
      selectedTicketId,
      setSelectedTicketId,
    ] = useState<
      number | null
    >(null);

    const [
      quantity,
      setQuantity,
    ] = useState(1);

    const [
      registrantName,
      setRegistrantName,
    ] = useState(
      getUserName(
        loggedUser,
      ),
    );

    const [
      mobileNumber,
      setMobileNumber,
    ] = useState(
      getUserMobile(
        loggedUser,
      ),
    );

    const [
      email,
      setEmail,
    ] = useState(
      getUserEmail(
        loggedUser,
      ),
    );

    const [
      remarks,
      setRemarks,
    ] = useState("");

    const [
      formError,
      setFormError,
    ] = useState<
      string | null
    >(null);

    const [
      localSuccess,
      setLocalSuccess,
    ] = useState<
      string | null
    >(null);

    const [
      openingCheckout,
      setOpeningCheckout,
    ] = useState(false);

    // ========================================================
    // LOAD CLUB
    // ========================================================

    useEffect(() => {
      if (slug) {
        dispatch(
          getClubProfileBySlug(
            slug,
          ),
        );
      }
    }, [
      dispatch,
      slug,
    ]);

    // ========================================================
    // LOAD EVENT THROUGH EVENT SLICE
    // ========================================================

    useEffect(() => {
      if (
        !clientId ||
        !programId
      ) {
        return;
      }

      dispatch(
        fetchEventDetails({
          client_id:
            clientId,

          event_id:
            programId,

          user_id:
            userId,
        }),
      );
    }, [
      dispatch,
      clientId,
      programId,
      userId,
    ]);

    // ========================================================
    // CLEAR EVENT STATE ON PAGE CHANGE / UNMOUNT
    // ========================================================

    useEffect(() => {
      return () => {
        dispatch(
          clearEventState(),
        );
      };
    }, [
      dispatch,
      eventId,
    ]);

    // ========================================================
    // PRESELECT FIRST ACTIVE TICKET
    // ========================================================

    const ticketTypes =
      useMemo(() => {
        const rows =
          Array.isArray(
            event?.ticket_types,
          )
            ? event.ticket_types
            : [];

        return rows.filter(
          (ticket: any) =>
            Number(
              ticket?.status ??
                1,
            ) === 1,
        );
      }, [
        event?.ticket_types,
      ]);

    useEffect(() => {
      if (
        selectedTicketId ||
        !ticketTypes.length
      ) {
        return;
      }

      const first =
        ticketTypes[0];

      setSelectedTicketId(
        Number(
          first.ticket_type_id,
        ),
      );
    }, [
      ticketTypes,
      selectedTicketId,
    ]);

    const selectedTicket =
      useMemo(
        () =>
          ticketTypes.find(
            (ticket: any) =>
              Number(
                ticket.ticket_type_id,
              ) ===
              Number(
                selectedTicketId,
              ),
          ) || null,
        [
          ticketTypes,
          selectedTicketId,
        ],
      );

    const ticketPrice =
      selectedTicket?.ticket_type ===
      "free"
        ? 0
        : Number(
            selectedTicket?.price ||
              0,
          );

    const totalAmount =
      Number(
        (
          ticketPrice *
          quantity
        ).toFixed(2),
      );

    const registrationRequired =
      Number(
        event?.registration_required ||
          0,
      ) === 1;

    const isAlreadyRegistered =
      Number(
        event?.is_registered ||
          0,
      ) === 1;

    const registrationPaid =
      String(
        registration?.payment_status ||
          paymentStatus
            ?.registration_payment_status ||
          "",
      ).toLowerCase() ===
        "paid" ||
      String(
        registration
          ?.registration_status ||
          paymentStatus
            ?.registration_status ||
          "",
      ).toLowerCase() ===
        "confirmed";

    // ========================================================
    // PAYMENT RETURN STATUS
    //
    // Cashfree backend appends order_id to return URL.
    // ========================================================

    const returnOrderId =
      Number(
        searchParams.get(
          "order_id",
        ) || 0,
      );

    useEffect(() => {
      if (!returnOrderId) {
        return;
      }

      dispatch(
        fetchEventPaymentStatus({
          order_id:
            returnOrderId,
        }),
      )
        .unwrap()
        .then(
          (
            result: any,
          ) => {
            const status =
              String(
                result?.order_status ||
                  result?.registration_payment_status ||
                  "",
              ).toLowerCase();

            if (
              status ===
                "paid" ||
              String(
                result?.registration_status ||
                  "",
              ).toLowerCase() ===
                "confirmed"
            ) {
              setLocalSuccess(
                "Payment completed successfully. Your event registration is confirmed.",
              );

              if (
                clientId &&
                programId
              ) {
                dispatch(
                  fetchEventDetails({
                    client_id:
                      clientId,
                    event_id:
                      programId,
                    user_id:
                      userId,
                  }),
                );
              }
            }
          },
        )
        .catch(() => {
          // Redux error state is displayed below.
        });
    }, [
      dispatch,
      returnOrderId,
      clientId,
      programId,
      userId,
    ]);

    // ========================================================
    // REGISTER
    // ========================================================

    const handleRegister =
      async () => {
        setFormError(
          null,
        );

        setLocalSuccess(
          null,
        );

        dispatch(
          clearEventError(),
        );

        if (!event) {
          setFormError(
            "Event details are unavailable.",
          );

          return;
        }

        if (
          !registrationRequired
        ) {
          setFormError(
            "Registration is not enabled for this event.",
          );

          return;
        }

        if (
          !selectedTicket
        ) {
          setFormError(
            "Please select a ticket.",
          );

          return;
        }

        if (
          !registrantName.trim()
        ) {
          setFormError(
            "Name is required.",
          );

          return;
        }

        if (
          !mobileNumber.trim()
        ) {
          setFormError(
            "Mobile number is required.",
          );

          return;
        }

        if (
          !Number.isInteger(
            quantity,
          ) ||
          quantity < 1
        ) {
          setFormError(
            "Quantity must be at least 1.",
          );

          return;
        }

        try {
          const registrationResult =
            await dispatch(
              registerForEvent({
                program_id:
                  Number(
                    event.program_id ||
                      programId,
                  ),

                client_id:
                  clientId,

                ticket_type_id:
                  Number(
                    selectedTicket
                      .ticket_type_id,
                  ),

                registrant_type:
                  getRegistrationType(
                    loggedUser,
                    event,
                  ),

                user_id:
                  userId ||
                  null,

                contact_id:
                  contactId ||
                  null,

                registrant_name:
                  registrantName.trim(),

                mobile_number:
                  mobileNumber.trim(),

                email:
                  email.trim() ||
                  null,

                quantity,

                remarks:
                  remarks.trim() ||
                  null,
              }),
            ).unwrap();

          if (
            !registrationResult
          ) {
            throw new Error(
              "Registration response is missing.",
            );
          }

          const amount =
            Number(
              registrationResult
                ?.total_amount ||
                0,
            );

          const registrationId =
            Number(
              registrationResult
                ?.registration_id ||
                0,
            );

          // FREE REGISTRATION
          if (amount <= 0) {
            setLocalSuccess(
              "Registration confirmed successfully.",
            );

            await dispatch(
              fetchEventDetails({
                client_id:
                  clientId,

                event_id:
                  programId,

                user_id:
                  userId,
              }),
            );

            return;
          }

          // PAID REGISTRATION
          if (
            !registrationId
          ) {
            throw new Error(
              "Registration ID is missing.",
            );
          }

          await startPaidCheckout({
            registrationId,
            amount,
          });
        } catch (
          submitError: any
        ) {
          setFormError(
            getErrorMessage(
              submitError,
              "Unable to complete registration.",
            ),
          );
        }
      };

    // ========================================================
    // CREATE PAYMENT ORDER + SESSION + OPEN CASHFREE
    // ========================================================

    const startPaidCheckout =
      async ({
        registrationId,
        amount,
      }: {
        registrationId: number;
        amount: number;
      }) => {
        setOpeningCheckout(
          true,
        );

        try {
          const returnUrl =
            `${window.location.origin}` +
            `/club/${encodeURIComponent(
              String(slug || ""),
            )}` +
            `/events/${encodeURIComponent(
              String(
                programId,
              ),
            )}`;

          const orderResult =
            await dispatch(
              createEventPaymentOrder({
                client_id:
                  clientId,

                registration_id:
                  registrationId,

                amount,

                payable_amount:
                  amount,

                payer_user_id:
                  userId ||
                  null,

                payer_contact_id:
                  contactId ||
                  null,

                payer_name:
                  registrantName.trim(),

                payer_mobile:
                  mobileNumber.trim(),

                payer_email:
                  email.trim() ||
                  null,

                source_app:
                  "public_web",

                source_platform:
                  "web",

                currency:
                  "INR",

                return_url:
                  returnUrl,
              }),
            ).unwrap();

          const orderId =
            Number(
              orderResult?.order_id ||
                0,
            );

          if (!orderId) {
            throw new Error(
              "Payment order ID is missing.",
            );
          }

          // Keep enough context if the user returns from checkout.
          sessionStorage.setItem(
            `pending_event_order_${programId}`,
            String(orderId),
          );

          sessionStorage.setItem(
            `pending_event_registration_${programId}`,
            String(
              registrationId,
            ),
          );

          const sessionResult =
            await dispatch(
              createEventPaymentSession({
                order_id:
                  orderId,

                return_url:
                  returnUrl,

                customer_name:
                  registrantName.trim(),

                customer_email:
                  email.trim() ||
                  undefined,

                customer_phone:
                  mobileNumber.trim(),
              }),
            ).unwrap();

          const paymentSessionId =
            sessionResult
              ?.payment_session_id;

          if (
            !paymentSessionId
          ) {
            throw new Error(
              "Cashfree payment session is missing.",
            );
          }

          const {
            load,
          } = await import(
            "@cashfreepayments/cashfree-js"
          );

          const cashfree =
            await load({
              mode:
                sessionResult
                  ?.gateway
                  ?.environment ===
                "production"
                  ? "production"
                  : "sandbox",
            });

          const checkoutResult =
            await cashfree.checkout({
              paymentSessionId,

              redirectTarget:
                "_self",
            });

          if (
            checkoutResult?.error
          ) {
            throw new Error(
              checkoutResult.error
                ?.message ||
                "Unable to open Cashfree checkout.",
            );
          }
        } finally {
          setOpeningCheckout(
            false,
          );
        }
      };

    // ========================================================
    // RETRY EXISTING PAYMENT ORDER
    // ========================================================

    const retryPendingPayment =
      async () => {
        const storedOrderId =
          Number(
            sessionStorage.getItem(
              `pending_event_order_${programId}`,
            ) || 0,
          );

        if (!storedOrderId) {
          setFormError(
            "No pending payment was found.",
          );

          return;
        }

        setOpeningCheckout(
          true,
        );

        setFormError(
          null,
        );

        try {
          const returnUrl =
            `${window.location.origin}` +
            `/club/${encodeURIComponent(
              String(slug || ""),
            )}` +
            `/events/${encodeURIComponent(
              String(
                programId,
              ),
            )}`;

          const sessionResult =
            await dispatch(
              createEventPaymentSession({
                order_id:
                  storedOrderId,

                return_url:
                  returnUrl,

                customer_name:
                  registrantName.trim() ||
                  undefined,

                customer_email:
                  email.trim() ||
                  undefined,

                customer_phone:
                  mobileNumber.trim() ||
                  undefined,
              }),
            ).unwrap();

          if (
            !sessionResult
              ?.payment_session_id
          ) {
            throw new Error(
              "Cashfree payment session is missing.",
            );
          }

          const {
            load,
          } = await import(
            "@cashfreepayments/cashfree-js"
          );

          const cashfree =
            await load({
              mode:
                sessionResult
                  ?.gateway
                  ?.environment ===
                "production"
                  ? "production"
                  : "sandbox",
            });

          await cashfree.checkout({
            paymentSessionId:
              sessionResult
                .payment_session_id,

            redirectTarget:
              "_self",
          });
        } catch (
          retryError: any
        ) {
          setFormError(
            getErrorMessage(
              retryError,
              "Unable to retry payment.",
            ),
          );
        } finally {
          setOpeningCheckout(
            false,
          );
        }
      };

    // ========================================================
    // PAYMENT STATUS DISPLAY
    // ========================================================

    const currentPaymentStatus =
      String(
        paymentStatus
          ?.order_status ||
          paymentStatus
            ?.registration_payment_status ||
          "",
      ).toLowerCase();

    const paymentPending =
      [
        "created",
        "initiated",
        "pending",
        "active",
      ].includes(
        currentPaymentStatus,
      );

    const loading =
      profileLoading ||
      eventLoading;

    const actionLoading =
      registrationLoading ||
      paymentOrderLoading ||
      paymentSessionLoading ||
      openingCheckout;

    // ========================================================
    // RENDER
    // ========================================================

    return (
      <Fragment>
        <Header />
        <Leftnav />

        <main
          className="main-content right-chat-active event-detail-page"
          style={{
            paddingLeft:
              "285px",
            paddingRight:
              "315px",
            paddingTop:
              "58px",
            minHeight:
              "100vh",
            transition:
              "all 0.3s ease",
          }}
        >
          <div className="middle-sidebar-bottom event-detail-middle-bottom">
            <div className="middle-sidebar-left event-detail-middle-left">
              <div className="event-detail-shell">
                <button
                  type="button"
                  className="detail-back-btn"
                  onClick={() =>
                    navigate(
                      `/club/${slug}`,
                    )
                  }
                >
                  <i className="feather-arrow-left" />
                  Back to Club
                </button>

                {loading ? (
                  <div className="detail-state-card">
                    <div className="spinner-border text-primary" />
                    <p>
                      Loading event details...
                    </p>
                  </div>
                ) : !event ? (
                  <div className="detail-state-card">
                    <div className="detail-state-icon">
                      <i className="feather-calendar" />
                    </div>

                    <h3>
                      Event not available
                    </h3>

                    <p>
                      {getErrorMessage(
                        error,
                        "This event may be private, assigned to other members, unavailable, or removed.",
                      )}
                    </p>
                  </div>
                ) : (
                  <>
                    <section className="event-detail-hero">
                      <h1>
                        {event.event_title ||
                          "Club Event"}
                      </h1>

                      <p>
                        {event.event_description ||
                          event.description ||
                          "Event information from the club."}
                      </p>

                      <div className="event-detail-chips">
                        <span>
                          <i className="feather-calendar" />
                          {formatDateTime(
                            event.from_date_time,
                          )}
                        </span>

                        {event.location && (
                          <span>
                            <i className="feather-map-pin" />
                            {event.location}
                          </span>
                        )}

                        <span>
                          <i className="feather-eye" />
                          {String(
                            event.visibility ||
                              "public",
                          )}
                        </span>
                      </div>
                    </section>

                    {localSuccess && (
                      <div className="event-alert success">
                        <i className="feather-check-circle" />
                        <div>
                          <strong>
                            Success
                          </strong>
                          <p>
                            {localSuccess}
                          </p>
                        </div>
                      </div>
                    )}

                    {formError && (
                      <div className="event-alert error">
                        <i className="feather-alert-circle" />
                        <div>
                          <strong>
                            Unable to continue
                          </strong>
                          <p>
                            {formError}
                          </p>
                        </div>
                      </div>
                    )}

                    {error &&
                      !formError && (
                        <div className="event-alert error">
                          <i className="feather-alert-circle" />
                          <div>
                            <strong>
                              Request failed
                            </strong>
                            <p>
                              {getErrorMessage(
                                error,
                              )}
                            </p>
                          </div>
                        </div>
                      )}

                    {returnOrderId >
                      0 && (
                      <div className="payment-status-strip">
                        <div>
                          <span>
                            Payment status
                          </span>
                          <strong>
                            {paymentStatusLoading
                              ? "Checking..."
                              : currentPaymentStatus ||
                                "pending"}
                          </strong>
                        </div>

                        {!paymentStatusLoading &&
                          paymentPending && (
                            <button
                              type="button"
                              onClick={() =>
                                dispatch(
                                  fetchEventPaymentStatus(
                                    {
                                      order_id:
                                        returnOrderId,
                                    },
                                  ),
                                )
                              }
                            >
                              Check Again
                            </button>
                          )}
                      </div>
                    )}

                    <section className="detail-card">
                      <div className="detail-section-heading">
                        <div>
                          <h2>
                            Event Information
                          </h2>
                          <p>
                            Date, venue and registration details.
                          </p>
                        </div>
                      </div>

                      <div className="detail-info-grid">
                        <div className="detail-info-item">
                          <label>
                            Starts
                          </label>
                          <strong>
                            {formatDateTime(
                              event.from_date_time,
                            )}
                          </strong>
                        </div>

                        <div className="detail-info-item">
                          <label>
                            Ends
                          </label>
                          <strong>
                            {formatDateTime(
                              event.to_date_time,
                            )}
                          </strong>
                        </div>

                        <div className="detail-info-item">
                          <label>
                            Venue
                          </label>
                          <strong>
                            {event.venue_name ||
                              event.location ||
                              "-"}
                          </strong>
                        </div>

                        <div className="detail-info-item">
                          <label>
                            Address
                          </label>
                          <strong>
                            {event.venue_address ||
                              "-"}
                          </strong>
                        </div>

                        <div className="detail-info-item">
                          <label>
                            Registration
                          </label>
                          <strong>
                            {registrationRequired
                              ? "Required"
                              : "Not required"}
                          </strong>
                        </div>

                        <div className="detail-info-item">
                          <label>
                            Visibility
                          </label>
                          <strong>
                            {String(
                              event.visibility ||
                                "public",
                            )}
                          </strong>
                        </div>
                      </div>
                    </section>

                    {(event.event_description ||
                      event.description) && (
                      <section className="detail-card">
                        <div className="detail-section-heading">
                          <div>
                            <h2>
                              About This Event
                            </h2>
                          </div>
                        </div>

                        <p className="detail-description">
                          {event.event_description ||
                            event.description}
                        </p>
                      </section>
                    )}

                    {registrationRequired && (
                      <section className="detail-card registration-card">
                        <div className="detail-section-heading registration-heading">
                          <div>
                            <h2>
                              Registration
                            </h2>
                            <p>
                              Select your ticket and complete registration.
                            </p>
                          </div>

                          {isAlreadyRegistered && (
                            <span className="registered-badge">
                              <i className="feather-check" />
                              Registered
                            </span>
                          )}
                        </div>

                        {ticketTypes.length ===
                        0 ? (
                          <div className="ticket-empty">
                            No active tickets are available.
                          </div>
                        ) : (
                          <>
                            <div className="ticket-list">
                              {ticketTypes.map(
                                (
                                  ticket: any,
                                ) => {
                                  const id =
                                    Number(
                                      ticket.ticket_type_id,
                                    );

                                  const active =
                                    id ===
                                    selectedTicketId;

                                  const price =
                                    ticket.ticket_type ===
                                    "free"
                                      ? 0
                                      : Number(
                                          ticket.price ||
                                            0,
                                        );

                                  return (
                                    <button
                                      key={
                                        id
                                      }
                                      type="button"
                                      className={`ticket-option ${
                                        active
                                          ? "active"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        setSelectedTicketId(
                                          id,
                                        );

                                        setQuantity(
                                          1,
                                        );

                                        dispatch(
                                          clearEventPayment(),
                                        );
                                      }}
                                    >
                                      <div>
                                        <strong>
                                          {ticket.ticket_name}
                                        </strong>

                                        <span>
                                          {ticket.description ||
                                            (ticket.ticket_type ===
                                            "free"
                                              ? "Free registration"
                                              : "Paid registration")}
                                        </span>
                                      </div>

                                      <b>
                                        {price >
                                        0
                                          ? formatMoney(
                                              price,
                                            )
                                          : "Free"}
                                      </b>
                                    </button>
                                  );
                                },
                              )}
                            </div>

                            <div className="registration-form-grid">
                              <div className="event-form-field full">
                                <label>
                                  Name
                                </label>
                                <input
                                  value={
                                    registrantName
                                  }
                                  onChange={(
                                    e,
                                  ) =>
                                    setRegistrantName(
                                      e.target
                                        .value,
                                    )
                                  }
                                  placeholder="Enter name"
                                />
                              </div>

                              <div className="event-form-field">
                                <label>
                                  Mobile Number
                                </label>
                                <input
                                  value={
                                    mobileNumber
                                  }
                                  onChange={(
                                    e,
                                  ) =>
                                    setMobileNumber(
                                      e.target
                                        .value,
                                    )
                                  }
                                  placeholder="Enter mobile number"
                                />
                              </div>

                              <div className="event-form-field">
                                <label>
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={
                                    email
                                  }
                                  onChange={(
                                    e,
                                  ) =>
                                    setEmail(
                                      e.target
                                        .value,
                                    )
                                  }
                                  placeholder="Email address"
                                />
                              </div>

                              <div className="event-form-field">
                                <label>
                                  Quantity
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={
                                    Number(
                                      selectedTicket?.registration_limit_per_user ||
                                        1,
                                    ) || 1
                                  }
                                  value={
                                    quantity
                                  }
                                  onChange={(
                                    e,
                                  ) => {
                                    const next =
                                      Number(
                                        e.target
                                          .value,
                                      );

                                    setQuantity(
                                      Number.isInteger(
                                        next,
                                      ) &&
                                        next >
                                          0
                                        ? next
                                        : 1,
                                    );
                                  }}
                                />
                              </div>

                              <div className="event-form-field">
                                <label>
                                  Ticket
                                </label>
                                <div className="readonly-value">
                                  {selectedTicket?.ticket_name ||
                                    "-"}
                                </div>
                              </div>

                              <div className="event-form-field full">
                                <label>
                                  Remarks
                                  <span>
                                    Optional
                                  </span>
                                </label>
                                <textarea
                                  rows={
                                    3
                                  }
                                  value={
                                    remarks
                                  }
                                  onChange={(
                                    e,
                                  ) =>
                                    setRemarks(
                                      e.target
                                        .value,
                                    )
                                  }
                                  placeholder="Add a note"
                                />
                              </div>
                            </div>

                            <div className="registration-summary">
                              <div>
                                <span>
                                  Ticket
                                </span>
                                <strong>
                                  {selectedTicket?.ticket_name ||
                                    "-"}
                                </strong>
                              </div>

                              <div>
                                <span>
                                  Quantity
                                </span>
                                <strong>
                                  {quantity}
                                </strong>
                              </div>

                              <div>
                                <span>
                                  Total
                                </span>
                                <strong className="summary-total">
                                  {totalAmount >
                                  0
                                    ? formatMoney(
                                        totalAmount,
                                      )
                                    : "Free"}
                                </strong>
                              </div>
                            </div>

                            {registrationPaid ? (
                              <div className="registration-confirmed">
                                <i className="feather-check-circle" />
                                Registration confirmed
                              </div>
                            ) : (
                              <div className="registration-actions">
                                <button
                                  type="button"
                                  className="register-main-btn"
                                  disabled={
                                    actionLoading
                                  }
                                  onClick={
                                    handleRegister
                                  }
                                >
                                  {actionLoading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm" />
                                      Processing...
                                    </>
                                  ) : totalAmount >
                                    0 ? (
                                    <>
                                      <i className="feather-credit-card" />
                                      Register & Pay{" "}
                                      {formatMoney(
                                        totalAmount,
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <i className="feather-check-circle" />
                                      Register Free
                                    </>
                                  )}
                                </button>

                                {sessionStorage.getItem(
                                  `pending_event_order_${programId}`,
                                ) && (
                                  <button
                                    type="button"
                                    className="retry-payment-btn"
                                    disabled={
                                      actionLoading
                                    }
                                    onClick={
                                      retryPendingPayment
                                    }
                                  >
                                    Retry Pending Payment
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </section>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </main>

        <Popupchat />
        <Appfooter />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

          .main-content.right-chat-active.event-detail-page {
            width: 100%;
            font-family: 'Inter', sans-serif;
            background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
            overflow-x: hidden;
          }

          .event-detail-middle-bottom,
          .event-detail-middle-left {
            width: 100%;
            max-width: 100%;
          }

          .event-detail-shell {
            margin-left: 22px;
            max-width: 900px;
            margin-top: 28px;
            padding-bottom: 40px;
          }

          .detail-back-btn {
            min-height: 34px;
            margin-bottom: 14px;
            padding: 0 11px;
            border: 1px solid #dce7f7;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            color: #2F6FE4;
            background: #ffffff;
            font-size: 11px;
            font-weight: 700;
            cursor: pointer;
          }

          .event-detail-hero {
            margin-bottom: 18px;
            padding: 16px;
            border: 1px solid #e2eaf6;
            border-radius: 16px;
            background: linear-gradient(135deg,#ffffff 0%,#f7faff 58%,#f1f6ff 100%);
            box-shadow: 0 5px 16px rgba(10,35,84,.045);
          }

          .event-detail-hero h1 {
            margin: 0 0 5px;
            color: #0A2354;
            font-family: 'Fraunces', serif;
            font-size: 24px;
            line-height: 1.25;
          }

          .event-detail-hero p {
            margin: 0;
            color: #7f899a;
            font-size: 13px;
            line-height: 1.55;
          }

          .event-detail-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 7px;
            margin-top: 11px;
          }

          .event-detail-chips span {
            min-height: 27px;
            padding: 0 9px;
            border: 1px solid #dce7f7;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            color: #5f7fae;
            background: rgba(255,255,255,.82);
            font-size: 10.5px;
            font-weight: 700;
            text-transform: capitalize;
          }

          .detail-card {
            margin-bottom: 18px;
            padding: 16px;
            border: 1px solid #e9ecf2;
            border-radius: 16px;
            background: #ffffff;
            box-shadow: 0 2px 8px rgba(15,23,42,.045);
          }

          .detail-section-heading {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 14px;
          }

          .detail-section-heading h2 {
            margin: 0;
            color: #0A2354;
            font-family: 'Fraunces', serif;
            font-size: 18px;
          }

          .detail-section-heading p {
            margin: 4px 0 0;
            color: #8a93a3;
            font-size: 12px;
          }

          .detail-info-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }

          .detail-info-item {
            min-width: 0;
            padding: 10px;
            border: 1px solid #edf1f6;
            border-radius: 10px;
            background: #f8fafc;
          }

          .detail-info-item label {
            display: block;
            margin-bottom: 4px;
            color: #94a3b8;
            font-size: 10.5px;
            font-weight: 600;
          }

          .detail-info-item strong {
            display: block;
            color: #0f1b34;
            font-size: 12.5px;
            font-weight: 600;
            line-height: 1.4;
            overflow-wrap: anywhere;
          }

          .detail-description {
            margin: 0;
            color: #657084;
            font-size: 12.5px;
            line-height: 1.7;
            white-space: pre-line;
          }

          .detail-state-card {
            width: 100%;
            min-height: 220px;
            padding: 28px 22px;
            border: 1px dashed #dbe2ec;
            border-radius: 16px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            background: #ffffff;
          }

          .detail-state-icon {
            width: 44px;
            height: 44px;
            margin-bottom: 11px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #5f7fae;
            background: #eef4fb;
            font-size: 20px;
          }

          .detail-state-card h3 {
            margin: 0;
            color: #475569;
            font-size: 14px;
          }

          .detail-state-card p {
            max-width: 420px;
            margin: 6px 0 0;
            color: #94a3b8;
            font-size: 11.5px;
            line-height: 1.55;
          }

          .event-alert {
            margin-bottom: 14px;
            padding: 11px 12px;
            border-radius: 12px;
            display: flex;
            align-items: flex-start;
            gap: 9px;
            font-size: 12px;
          }

          .event-alert i {
            margin-top: 2px;
            font-size: 16px;
          }

          .event-alert strong {
            display: block;
            margin-bottom: 2px;
          }

          .event-alert p {
            margin: 0;
            line-height: 1.5;
          }

          .event-alert.success {
            border: 1px solid #ccebd7;
            color: #237a48;
            background: #f3fbf6;
          }

          .event-alert.error {
            border: 1px solid #f0d5d5;
            color: #a34848;
            background: #fff7f7;
          }

          .payment-status-strip {
            margin-bottom: 14px;
            padding: 11px 13px;
            border: 1px solid #dce7f7;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: #f8fbff;
          }

          .payment-status-strip span {
            display: block;
            color: #8794a8;
            font-size: 10px;
          }

          .payment-status-strip strong {
            color: #0A2354;
            font-size: 12px;
            text-transform: capitalize;
          }

          .payment-status-strip button {
            border: 0;
            background: transparent;
            color: #2F6FE4;
            font-size: 11px;
            font-weight: 700;
            cursor: pointer;
          }

          .registration-card {
            border-color: #dfe9f7;
          }

          .registration-heading {
            align-items: center;
          }

          .registered-badge {
            padding: 5px 8px;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            color: #26774b;
            background: #edf9f2;
            font-size: 10px;
            font-weight: 700;
          }

          .ticket-list {
            display: grid;
            gap: 8px;
            margin-bottom: 15px;
          }

          .ticket-option {
            width: 100%;
            padding: 11px 12px;
            border: 1px solid #e6ebf2;
            border-radius: 11px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
            text-align: left;
            background: #fbfcfe;
            cursor: pointer;
            transition: .18s ease;
          }

          .ticket-option.active {
            border-color: #9ebcf1;
            background: #f3f7ff;
            box-shadow: inset 0 0 0 1px rgba(47,111,228,.06);
          }

          .ticket-option strong {
            display: block;
            margin-bottom: 2px;
            color: #18243a;
            font-size: 12.5px;
          }

          .ticket-option span {
            color: #8a94a4;
            font-size: 10.5px;
          }

          .ticket-option b {
            flex-shrink: 0;
            color: #225ebc;
            font-size: 12px;
          }

          .ticket-empty {
            padding: 18px;
            border: 1px dashed #dde4ec;
            border-radius: 11px;
            color: #8792a3;
            text-align: center;
            font-size: 12px;
          }

          .registration-form-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          .event-form-field.full {
            grid-column: 1 / -1;
          }

          .event-form-field label {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
            color: #596579;
            font-size: 10.5px;
            font-weight: 700;
          }

          .event-form-field label span {
            color: #a1a9b7;
            font-weight: 500;
          }

          .event-form-field input,
          .event-form-field textarea,
          .readonly-value {
            width: 100%;
            border: 1px solid #dde5ef;
            border-radius: 9px;
            outline: none;
            background: #fff;
            color: #15213a;
            font-family: inherit;
            font-size: 12px;
          }

          .event-form-field input,
          .readonly-value {
            min-height: 38px;
            padding: 0 10px;
          }

          .event-form-field textarea {
            padding: 9px 10px;
            resize: vertical;
          }

          .readonly-value {
            display: flex;
            align-items: center;
            background: #f8fafc;
          }

          .event-form-field input:focus,
          .event-form-field textarea:focus {
            border-color: #9cb9ec;
            box-shadow: 0 0 0 3px rgba(47,111,228,.07);
          }

          .registration-summary {
            margin-top: 13px;
            padding: 11px 12px;
            border: 1px solid #e7ecf2;
            border-radius: 11px;
            display: grid;
            grid-template-columns: 1.5fr .6fr .8fr;
            gap: 10px;
            background: #fafbfd;
          }

          .registration-summary span {
            display: block;
            margin-bottom: 3px;
            color: #95a0b0;
            font-size: 9.5px;
          }

          .registration-summary strong {
            color: #24324a;
            font-size: 11.5px;
          }

          .summary-total {
            color: #225ebc !important;
          }

          .registration-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 12px;
          }

          .register-main-btn {
            min-height: 38px;
            padding: 0 15px;
            border: 0;
            border-radius: 9px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            background: #2F6FE4;
            color: #fff;
            font-size: 11.5px;
            font-weight: 700;
            cursor: pointer;
          }

          .register-main-btn:disabled,
          .retry-payment-btn:disabled {
            opacity: .6;
            cursor: not-allowed;
          }

          .retry-payment-btn {
            min-height: 38px;
            padding: 0 12px;
            border: 1px solid #dbe5f3;
            border-radius: 9px;
            background: #fff;
            color: #54729f;
            font-size: 11px;
            font-weight: 700;
            cursor: pointer;
          }

          .registration-confirmed {
            margin-top: 12px;
            min-height: 40px;
            padding: 0 12px;
            border: 1px solid #cfe9d9;
            border-radius: 10px;
            display: flex;
            align-items: center;
            gap: 7px;
            color: #26774b;
            background: #f3faf6;
            font-size: 11.5px;
            font-weight: 700;
          }

          @media (max-width: 1400px) {
            .main-content.right-chat-active.event-detail-page {
              padding-right: 290px !important;
            }
          }

          @media (max-width: 1200px) {
            .main-content.right-chat-active.event-detail-page {
              padding-left: 20px !important;
              padding-right: 20px !important;
            }

            .event-detail-shell {
              margin-left: 0;
              max-width: 100%;
            }
          }

          @media (max-width: 768px) {
            .main-content.right-chat-active.event-detail-page {
              padding-top: 16px !important;
              padding-left: 12px !important;
              padding-right: 12px !important;
            }

            .event-detail-shell {
              margin-top: 0;
              margin-left: 0;
              max-width: 100%;
              padding-bottom: 76px;
            }

            .event-detail-hero,
            .detail-card {
              padding: 13px;
              border-radius: 14px;
            }

            .event-detail-hero h1 {
              font-size: 21px;
            }

            .detail-info-grid,
            .registration-form-grid,
            .registration-summary {
              grid-template-columns: 1fr;
            }

            .event-form-field.full {
              grid-column: auto;
            }

            .registration-actions {
              align-items: stretch;
              flex-direction: column;
            }

            .register-main-btn,
            .retry-payment-btn {
              width: 100%;
            }

            .payment-status-strip {
              align-items: flex-start;
              gap: 8px;
            }
          }
        `}</style>
      </Fragment>
    );
  };

export default EventDetailsPage;
